import { CustomerData, CustomerDataFields, CustomerDataOptionsPayTyp } from "../../interfaces/CustomerData/CustomerData";
import { OrderDataProducts } from "../../interfaces/OrderData/OrderData";
import ProductPrice from "../../interfaces/ProductPrice/ProductPrice";
import ProductProperties, { FormFieldName } from "../../interfaces/ProductProperties/ProductProperties";
import ProductPropertiesRelated, { ProductType } from "../../interfaces/ProductProperties/ProductPropertiesRelated";
import localStorageProvider from "../LocalStorageProvider/LocalStorageProvider";
import loginProvider from "../LoginProvider/LoginProvider";
import requestProvider from "../RequestProvider/RequestProvider";
import UtilityProviderProps from "./UtilityProvider.props";

export class UtilityProvider {
    private props: UtilityProviderProps;

    constructor(props: UtilityProviderProps) {
        this.props = props;
        this.setRootElement = this.setRootElement.bind(this);
        this.dispatchEventReactAppInitialized = this.dispatchEventReactAppInitialized.bind(this);
    }

    setRootElement(rootElement: HTMLElement) {
        this.props.rootElement = rootElement;
    }

    dispatchEventReactAppInitialized() {
        this.props.rootElement && this.props.rootElement.dispatchEvent(new Event('react_app_created'));
    }
    
    componentToJson(component: React.Component) {
        return JSON.stringify({
            [component.constructor.name]: component.state
        });
    }

    getObjectFromForm(form: HTMLFormElement | null): unknown {
        if (form === null) {
            return {};
        }

        const formDataFromForm = Array.from(new FormData(form || undefined).entries());
        const formData: Record<string, string | string[]> = {};
        formDataFromForm.forEach((formField) => {
            const formFieldName: string = formField[0];
            const formFieldValue: string = formField[1] as string;

            if (formFieldName === CustomerDataFields.INTERESTS ||
                formFieldName === CustomerDataFields.MEMBERSHIPS ||
                formFieldName === CustomerDataFields.SOLICITOR) {
                formData[formFieldName] = formData[formFieldName] || [];
                (formData[formFieldName] as string[]).push(formFieldValue);
            } else {
                formData[formFieldName] = formFieldValue;
            }
        });
        return formData;
    }


    getPremiumIndexFromProductProperties(productProperties: ProductProperties): number {
        return productProperties.relatedProducts.findIndex((product) => product.productType === ProductType.PREMIUM);
    }

    getPremiumProductFromProductProperties(productProperties: ProductProperties): ProductPropertiesRelated | undefined {
        return productProperties.relatedProducts.find((product) => product.productType === ProductType.PREMIUM);
    }

    getBaseProductFromProductProperties(productProperties: ProductProperties): ProductPropertiesRelated | undefined {
        return productProperties.relatedProducts.find((product) => product.productType === ProductType.BASE);
    }

    getForLawyerProductFromProductProperties(productProperties: ProductProperties): ProductPropertiesRelated | undefined {
        return productProperties.relatedProducts.find((product) => product.productType === ProductType.FOR_LAWYER);
    }

    getForOtherProductFromProductProperties(productProperties: ProductProperties): ProductPropertiesRelated | undefined {
        return productProperties.relatedProducts.find((product) => product.productType === ProductType.FOR_OTHER);
    }

    getOrderDataProductsFromProductPropertiesArray(productProperties: ProductProperties[], campaignId?: string, frameworkContract?: string): OrderDataProducts[] {
        
        const orderDataProducts:OrderDataProducts[] = [];

        for (let i = 0; i < productProperties.length; i++) {
            const product = productProperties[i];
            let orderDataProductsItem: OrderDataProducts;

            if (campaignId && !frameworkContract) {
                orderDataProductsItem = {
                    magicNumber: product.magicNumber,
                    additionalParameters: {
                        campaignCode: campaignId
                    },
                };
            } else if (campaignId && frameworkContract) {
                orderDataProductsItem = {
                    magicNumber: product.magicNumber,
                    additionalParameters: {
                        campaignCode: campaignId,
                        frameworkContract: frameworkContract
                    },
                };
            } else {
                orderDataProductsItem = {
                    magicNumber: product.magicNumber,
                    additionalParameters: {
                        campaignCode: "KA14120"
                    },
                };
            }

            if (product.configOptions) {
                for (let k = 0; k < product.configOptions?.length; k++) {
                    const configOption = product.configOptions[k];
                    
                    if (configOption.name === FormFieldName.NUMBEROFLICENCES) {
                        orderDataProductsItem.numberOfLicences = Number(configOption.value);
                        continue;
                    }
                    
                    if (configOption.name === FormFieldName.PAYMENTPERIOD) {
                        orderDataProductsItem.paymentPeriod = configOption.value;
                        continue;
                    }
    
                    orderDataProductsItem.additionalParameters[configOption.name] = configOption.value;
                }
            }

            orderDataProducts.push(orderDataProductsItem);
        }

        return orderDataProducts;
    }

    async getPrice(productProperties?: ProductProperties): Promise<ProductPrice> {
        const result = {
            magicNumber: productProperties?.magicNumber || '',
            price: 0.0,
            priceInclMwst: 0.0,
            paymentPeriod: '1M'
        } as ProductPrice;

        if (productProperties === undefined) {
            return result;
        }

        let url = `/order-api/price?magicNumber=${productProperties?.magicNumber}`;
        productProperties?.configOptions?.forEach((formField) => {
            if (formField.name === FormFieldName.PRECONDITIONSELFDISCLOSURE || formField.name === FormFieldName.FRAMEWORKCONTRACT) {
                return;
            }

            if (formField.name === FormFieldName.PAYMENTPERIOD) {
                result.paymentPeriod = formField.value
            }

            url += `&${formField.name}=${formField.value === undefined ? 'false' : formField.value}`;
        });
        const priceRequest = await requestProvider.getData(url);

        if (priceRequest === undefined) {
            return result;
        }

        result.price = (priceRequest as unknown as ProductPrice).price;
        result.priceInclMwst = (priceRequest as unknown as ProductPrice).priceInclMwst;

        return result;
    }

    orderHasTestproduct(order: ProductProperties[]): boolean {
        if (order) {
            return order.findIndex(product => product.productType === ProductType.TEST) >= 0;
        }
        return false;
    }

    orderHasBuyableProduct(order: ProductProperties[]): boolean {
        if (order) {
            return order.findIndex(product => product.productType !== ProductType.TEST) >= 0;
        }
        return false;
    }

    orderHasMoreThanOneBuyableProduct(order: ProductProperties[]): boolean {
        if (order) {
            return order.filter(product => product.productType !== ProductType.TEST).length > 1;
        }
        return false;
    }

    // kein ProductType für POB Rückfrage MARTP-964 -> soll ignoriert werden
    orderHasPOB(order: ProductProperties[]): boolean {
        return true;

        // const warenkorbData = this.context.sessionStorageProvider.getProductInformationFromSessionStorage()?.warenkorbData;
        // if (warenkorbData) {
        //     return warenkorbData.findIndex(product => product.productType === ProductType.TEST) >= 0;
        // }
        // return false;
    }

    orderHasMoreThanOneUser(order: ProductProperties[]): boolean {
        if (order) {
            return order.filter(
                product => product.configOptions && product.configOptions.filter(
                    option => option.name === FormFieldName.NUMBEROFLICENCES && Number(option.value) > 1
                ).length > 0
            ).length > 0;
        }
        return false;
    }

    orderWithNewRegistration(customerData: CustomerData): boolean {
        return customerData.contactNo === undefined || loginProvider.hasJWTCookie() === false;
    }

    orderWithDebit(customerData: CustomerData): boolean {
        return customerData.payType === CustomerDataOptionsPayTyp.DEBIT;
    }

    inLSExistsRelatedProductFromThisProduct(productProperties?: ProductProperties, productPropertiesFromLocalStorage?: {
        productProperties?: ProductProperties;
        warenkorbData: ProductProperties[];
    }): boolean {
        let relatedProductExistsInLS = false;
        
        if (productProperties === undefined) {
            return relatedProductExistsInLS;
        }

        productPropertiesFromLocalStorage = productPropertiesFromLocalStorage 
            || localStorageProvider.getProductInformationFromLocalStorage(productProperties.magicNumber);

        productProperties.relatedProducts = productProperties.relatedProducts || []
        productProperties.relatedProducts.every((relatedProduct) => {
            productPropertiesFromLocalStorage?.warenkorbData?.every((productInformation) => {
                if (relatedProduct.magicNumber === productInformation.magicNumber) {
                    relatedProductExistsInLS = true;
                }
                return !relatedProductExistsInLS;
            });
            return !relatedProductExistsInLS;
        });
        return relatedProductExistsInLS;
    }

    inLSExistsBlacklistProductFromThisProduct(productProperties?: ProductProperties, productPropertiesFromLocalStorage?: {
        productProperties?: ProductProperties;
        warenkorbData: ProductProperties[];
    }): boolean {
        let blacklistProductExistsInLS = false;
        
        if (productProperties === undefined) {
            return blacklistProductExistsInLS;
        }

        productPropertiesFromLocalStorage = productPropertiesFromLocalStorage 
            || localStorageProvider.getProductInformationFromLocalStorage(productProperties.magicNumber);

        productProperties.blacklistIds = productProperties.blacklistIds || []
        productProperties.blacklistIds.every((blacklistProduct) => {
            productPropertiesFromLocalStorage?.warenkorbData?.every((productInformation) => {
                if (blacklistProduct === productInformation.magicNumber) {
                    blacklistProductExistsInLS = true;
                }
                return !blacklistProductExistsInLS;
            });
            return !blacklistProductExistsInLS;
        });
        return blacklistProductExistsInLS;
    }

    inLSExistsBlacklistOrRelatedProductFromThisProduct(productProperties?: ProductProperties, productPropertiesFromLocalStorage?: {
        productProperties?: ProductProperties;
        warenkorbData: ProductProperties[];
    }): boolean {
        if (productProperties === undefined) {
            return false;
        }

        productPropertiesFromLocalStorage = productPropertiesFromLocalStorage 
            || localStorageProvider.getProductInformationFromLocalStorage(productProperties.magicNumber);

        return this.inLSExistsBlacklistProductFromThisProduct(productProperties, productPropertiesFromLocalStorage) 
            || this.inLSExistsRelatedProductFromThisProduct(productProperties, productPropertiesFromLocalStorage);
    }

    inLSExistsSameProductAsThisProduct(productProperties?: ProductProperties, productPropertiesFromLocalStorage?: {
        productProperties?: ProductProperties;
        warenkorbData: ProductProperties[];
    }): boolean {
        if (productProperties === undefined) {
            return false;
        }

        productPropertiesFromLocalStorage = productPropertiesFromLocalStorage 
            || localStorageProvider.getProductInformationFromLocalStorage(productProperties.magicNumber);

        if (productPropertiesFromLocalStorage === undefined) {
            return false;
        }

        let doesProductExist = false;
        productPropertiesFromLocalStorage.warenkorbData.forEach((item, index) => {
            if (item.magicNumber === productProperties.magicNumber) {
                doesProductExist = true;
            } else {
                doesProductExist = false;
            }
        })

        return doesProductExist;
    }
}


const utilityProvider = new UtilityProvider({})
export default utilityProvider;
